import React from "react";
import PropTypes from "prop-types";
import typeOfOperand from "@tinqin/tinqin-utils/src/base/typeOfOperand";
import Preloader from "../preloader/Preloader";
/*
General Purpose Loading Animation (Higher Order Component)
-provides state management for a Loading Animation
-can be used as wrapper for custom loading animations
Features:
-min Loading time feature
    (will not be shown unless enabled for more than predefined time)
-fadein feature
    (once shown will fadein for predefined amount of time, before changes can be made)
-wait for content render feature
    (once disabled will wait for predefined amount of time before fadeout is triggered
     to allow for fadein animation to be rendered more smoothly after inner content is rendered)
-fadeout feature
    (once disabled will fadeout for predefined amount of time, before being hidden)
Loading animation states:
    hide->wait->fadein->show->waitContentRender->fadeout->hide
Accepted props:
    isLoading - control flag for changing loading animation states
    minLoadingTime
    contentRenderWaitingTime
    //All the rest properties will be passed down to WrappedLoadingAnimation
Usage:
    wrapLoadingAnimation(WrappedLoadingAnimation, options)
    -WrappedLoadingAnimation: customLoadingAnimation
        will receive as props:
            -status(one of the possible states),
            -all passed props like minHeight and extraClasses
    -options (can be a function, all options are numbers in milliseconds):
        minLoadingTime,
        fadeinTime,
        fadeoutTime,
        contentRenderWaitingTime
Example: tinqin-web-ui -> widget-loading/WidgetLoading.js
*/
const defaultLoadingTimes = {
    minLoadingTime: 250,
    fadeinTime: 500,
    fadeoutTime: 500,
    contentRenderWaitingTime: 50
};
export default function wrapLoadingAnimation(WrappedLoadingAnimation, options) {
    class LoadingAnimation extends React.Component {
        constructor(props) {
            super(props);
            this.state = this.getInitialState(props);
            if (typeOfOperand(options) === "function") {
                options = options();
            }
        }
        getInitialState(props) {
            let initialState = {
                status: "hide",
                //waiting time
                waiting: false,
                waitTimer: 0,
                //fadein time
                fadein: false,
                fadeinTimer: 0,
                //wait content render time
                waitContentRender: false,
                waitContentRenderTimer: 0,
                //fadeout time
                fadeout: false,
                fadeoutTimer: 0
            };
            if (props.isLoading) {
                let minLoadingTime = this.getTimeValue("minLoadingTime");
                if (minLoadingTime > 0) {
                    initialState.status = "wait";
                    initialState.waitTimer = setTimeout(() => this.endWaiting(), minLoadingTime);
                } else {
                    let fadeinTime = this.getTimeValue("fadeinTime");
                    if (fadeinTime > 0) {
                        initialState.status = "fadein";
                        initialState.fadeinTimer = setTimeout(() => this.endFadeIn(), fadeinTime);
                    } else {
                        initialState.status = "show";
                    }
                }
            }
            return initialState;
        }
        //in case component is unmounted during wait,fadein,fadeout transitions
        componentWillUnmount() {
            clearTimeout(this.state.waitTimer);
            clearTimeout(this.state.fadeinTimer);
            clearTimeout(this.state.waitContentRenderTimer);
            clearTimeout(this.state.fadeoutTimer);
        }
        //cases:
        //hide && isLoading -> startWaiting
        //show && !isLoading -> startFadeout
        componentDidUpdate(prevProps) {
            if (this.props.isLoading !== prevProps.isLoading) {
                let showLoading = this.state.status === "hide" && this.props.isLoading;
                let hideLoading = this.state.status === "show" && !this.props.isLoading;
                if (showLoading) {
                    this.startWaiting();
                }
                if (hideLoading) {
                    this.startWaitingContentRender();
                }
            }
        }
        getTimeValue(prop) {
            if (this.props.hasOwnProperty(prop)) {
                return this.props[prop];
            }
            if (options.hasOwnProperty(prop)) {
                return options[prop];
            }
            return defaultLoadingTimes[prop];
        }
        show() {
            this.setState({
                status: "show"
            });
        }
        hide() {
            this.setState({
                status: "hide"
            });
        }
        startWaiting() {
            let minLoadingTime = this.getTimeValue("minLoadingTime");
            if (minLoadingTime > 0) {
                this.setState({
                    status: "wait",
                    waitTimer: setTimeout(() => this.endWaiting(), minLoadingTime)
                });
            } else this.endWaiting();
        }
        endWaiting() {
            if (this.props.isLoading) {
                this.startFadeIn();
            } else {
                this.hide();
            }
        }
        startFadeIn() {
            let fadeinTime = this.getTimeValue("fadeinTime");
            if (fadeinTime > 0) {
                this.setState({
                    status: "fadein",
                    fadeinTimer: setTimeout(() => this.endFadeIn(), fadeinTime)
                });
            } else this.endFadeIn();
        }
        endFadeIn() {
            if (this.props.isLoading) {
                this.show();
            } else {
                this.startFadeOut();
            }
        }
        startWaitingContentRender() {
            let waitTime = this.getTimeValue("contentRenderWaitingTime");
            this.setState({
                status: "waitContentRender",
                waitContentRenderTimer: setTimeout(() => this.startFadeOut(), waitTime)
            });
        }
        startFadeOut() {
            let fadeoutTime = this.getTimeValue("fadeoutTime");
            setTimeout(() => {
                this.setState({
                    status: "fadeout",
                    fadeoutTimer: setTimeout(() => this.endFadeOut(), fadeoutTime)
                });
            }, 100);
        }
        endFadeOut() {
            if (this.props.isLoading) {
                this.startFadeIn();
            } else {
                this.hide();
            }
        }
        render() {
            //remove LoadingAnimationHOC specific props(isLoading, minLoadingTime, contentRenderWaitingTime)
            // and pass all the rest(extraClasses, minHeight etc.) to wrapped component
            let props = Object.assign({}, options, this.props);
            let passThroughProps = {};
            Object.keys(Preloader.propTypes).forEach(key => {
                if (props.hasOwnProperty(key)) {
                    passThroughProps[key] = props[key];
                }
            });
            return (
                <WrappedLoadingAnimation status={this.state.status} {...passThroughProps}>
                    {this.props.children}
                </WrappedLoadingAnimation>
            );
        }
    }

    LoadingAnimation.propTypes = {
        //control flag for changing loading animation states
        isLoading: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
        //have higher priority over default value and wrapped loading animation value
        minLoadingTime: PropTypes.number,
        //have higher priority over default value and wrapped loading animation value
        contentRenderWaitingTime: PropTypes.number,
        fadeinTime: PropTypes.number,
        fadeoutTime: PropTypes.number
        //Examples for props passed to wrapped loading animation:
        // minHeight: PropTypes.string,
        // extraClasses: PropTypes.string
    };

    return LoadingAnimation;
}
